import { CommonDialogs } from "@mcleod/common";
import { ClickEvent, Dialog, ImageViewer, Label, Layout, Snackbar, Tab, Table, TableRow, Toast } from "@mcleod/components";
import { Api, FileUtil, Model, ModelRow, StringUtil, getLogger } from "@mcleod/core";
import { OrderImages } from "./OrderImages";
import { AutogenLayoutImageTable } from "./autogen/AutogenLayoutImageTable";

const log = getLogger("lme.general.ImageTable");

export class ImageTable extends AutogenLayoutImageTable {
    private _orderId: string;
    private imageDialog: Dialog;

    override onLoad() {
        const imagesTab = this._getImagesTab();
        if (imagesTab == null)
            return;
        if (imagesTab.heading != null)
            imagesTab.heading.borderWidth = 0;
        if (imagesTab.headingLabel != null)
            imagesTab.headingLabel.color = "default";
    }

    private _getImagesTab(): Tab {
        return this["tabImages"] as Tab;
    }

    private _getImagesTable(): Table {
        return this["tableImages"] as Table;
    }

    populateForOrder(orderId: string) {
        this._orderId = orderId;
        this.mainDataSource.search({ row_type: "O", row_id: orderId })
            .then(r => this._updateImageTableHeader())
            .catch(error => this._updateImageTableHeader());
    }

    private _updateImageTableHeader() {
        const imagesTab = this._getImagesTab();
        if (imagesTab == null)
            return;
        if (imagesTab.headingLabel != null)
            imagesTab.headingLabel.fillRow = false;
        if (imagesTab.heading != null) {
            if (imagesTab["countLabel"] == null) {
                imagesTab["countLabel"] = new Label({
                    fontBold: true, caption: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                imagesTab.heading.insert(imagesTab["countLabel"], 1);
            }
            imagesTab["countLabel"].caption = this._getImagesTable().rowCount;
        }
    }

    labelDescrOnClick(event: ClickEvent) {
        const descrLabel = event.target as Label;
        const tableRow = TableRow.getContainingTableRow(descrLabel);
        const id = (tableRow.data as ModelRow).get("id");
        Model.searchSingleRecord("lme/imaging/get-image", { id: id }).then(row => {
            const imageBase64 = row.get("image");
            let imageFileName = row.get("filename", "");
            if (StringUtil.isEmptyString(imageFileName)) {
                const descrField = tableRow.findComponentsByField("descr")[0] as Label;
                imageFileName = this._orderId + "_" + descrField.caption + row.get("file_ext", ".image")
            }
            this.saveOrView(imageBase64, imageFileName);
        }).catch(error => {
            CommonDialogs.showError(error);
        });
    }

    private saveOrView(imageBase64: string, fileName: string): void {
        if (ImageViewer.eligibleToView(fileName) === true)
            ImageViewer.viewImage(imageBase64, fileName);
        else {
            FileUtil.downloadBase64AsFile(imageBase64, fileName);
            Snackbar.showSnackbar("Downloaded " + fileName);
        }
    }

    buttonAddImageOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/general/OrderImages", {
            maxWidth: 1100, width: window.innerWidth * .55,
            backgroundColor: "defaultBackground",
            borderRadius: 4, borderWidth: 1, borderShadow: true, borderColor: "strokeSecondary"
        }) as OrderImages;

        this.setupImages(layout);
        this.imageDialog = CommonDialogs.createDialog(layout, { title: "Upload Images", okVisible: false });
        this.imageDialog.show();
    }

    public setupImages(layout: OrderImages) {
        layout.addLayoutDataLoadListener(event => {
            const body = layout.createJSONStringForPOST(this._orderId);
            Api.search("lme/imaging/add-order-image", body).then(response => {
                if (response?.data[0]?.["success"]) layout.createDropdownItems(response.data[0]["document_types"]);
                else Toast.showToast("Error: Could not obtain list of valid image types", { backgroundColor: "error" });

                log.debug(response);
            });

            layout.buttonCancel.addClickListener(event => {
                this.imageDialog.close();
            });

            layout.buttonSave.addClickListener(event => {
                const body = layout.createJSONStringForPOST(this._orderId);
                Api.post("lme/imaging/add-order-image", body).then(response => {
                    if (response?.data[0]?.["success"]) {
                        this.populateForOrder(this._orderId);
                        layout.clearInputFields();
                        Toast.showToast("Your image has been queued for uploading. This process could take several minutes.");
                    }
                    else Toast.showToast("Error: Could not create image file", { backgroundColor: "error" });

                    log.debug(response);
                });
            });

            layout.buttonSaveAndClose.addClickListener(event => {
                const body = layout.createJSONStringForPOST(this._orderId);
                Api.post("lme/imaging/add-order-image", body).then(response => {
                    if (response?.data[0]?.["success"]) {
                        this.populateForOrder(this._orderId);
                        this.imageDialog.close();
                        Toast.showToast("Your image has been queued for uploading. This process could take several minutes.");
                    }
                    else Toast.showToast("Error: Could not create image file", { backgroundColor: "error" });

                    log.debug(response);
                });
            });
        });
    }
}
